import React, { useContext, useEffect, useState } from 'react'
import AuthContext from '../../context/AuthContext';
import ModalContext from '../../context/ModalContext';
import DrawerContext from '../../context/DrawerContext';
import CustomButton from '../../common/CustomButton';
import PageSearchBar from '../../common/PageSearchBar';
import './Administrator.scss';

//MUI
import { useTheme } from '@emotion/react';
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage, MoreVertRounded } from '@mui/icons-material';
import { CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import { Box } from '@mui/system';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fade from '@mui/material/Fade';

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}

export default function Administrator() {
  const modalCtx = useContext(ModalContext);
  const drawerCtx = useContext(DrawerContext);
  const authCtx = useContext(AuthContext);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [administratorsData, setAdministratorsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [displayedData, setDisplayedData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [updatingRows, setUpdatingRows] = useState(new Set());

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - displayedData.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getAllAdministrators = () => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URI}/v3/admin/administrators`, {
      method: 'GET',
      headers: { 
        'Content-Type': 'application/json', 
        'Authorization': `Bearer ${authCtx.token}`
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.status === 'success') {
        setAdministratorsData(data.data);
      }
      setLoading(false);
    })
    .catch(err => {
      console.error(err);
      setLoading(false);
    });
  }

  useEffect(() => {
    getAllAdministrators();
  }, []);

  useEffect(() => {
    const filteredData = administratorsData.filter((data) => (
      (data.name || '').toLowerCase().includes(searchInput.toLowerCase()) ||
      (data.email || '').toLowerCase().includes(searchInput.toLowerCase())
    ));
    setDisplayedData(filteredData);
  }, [searchInput, administratorsData]);

  const handleModal = (modal, data) => {
    handleClose();
    modalCtx.openModal();
    modalCtx.setDetails(modal, data);
  }

  useEffect(() => {
    if (modalCtx.pageUpdate?.page === "Administrators") {
      getAllAdministrators();
      modalCtx.setPageUpdate({ page: "" });
    }
  }, [modalCtx.pageUpdate]);

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatus = (access) => {
    if (access === 'admin') {
      return <div className='status-draft'>Admin</div>;
    } else if (access === 'superadmin') {
      return <div className='status-pending'>Super</div>;
    } else {
      return <div className='status-draft'>{access}</div>;
    }
  };

  return (
    <>
      <div className="administrators-container">
        <div className='header-section'>
          <span className='title'>ADMINISTRATORS</span>
          <div className='header-items-div'>
            <PageSearchBar value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search administrators" />
            <CustomButton className="add-btn" onClick={() => { handleModal('add-administrator', {}) }}>
              + Add Administrator
            </CustomButton>
          </div>
        </div>

        <TableContainer component={Paper} className='listing-table administrator-table'>
          <Table sx={{ minWidth: 500 }} stickyHeader>
            <TableHead>
              <TableRow className='table-head'>
                <TableCell align="left" className='head-cell administrator-head-cell'>Name</TableCell>
                <TableCell align="left" className='head-cell administrator-head-cell'>Email</TableCell>
                <TableCell align="left" className='head-cell administrator-head-cell'>Role</TableCell>
                <TableCell align="right" className='head-cell administrator-head-cell'></TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={loading ? "data-loading-circle" : "administrator-table-body"}>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" style={{ border: "0px", width: "100%", paddingTop: "100px" }}>
                    <CircularProgress size={52} style={{ color: "#2b1de8", border: "0px" }} />
                  </TableCell>
                </TableRow>
              ) : (
                (rowsPerPage > 0 ? displayedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : displayedData).map((data, index) => (
                  <TableRow 
                    key={data.id} 
                    className={`administrator-table-row ${updatingRows.has(data.id) ? "updating-row" : ""}`} 
                    style={{ backgroundColor: index % 2 !== 0 ? '#fafafc' : 'white' }}
                  >
                    <TableCell className='table-cell administrator-table-cell' align="left">{data.name}</TableCell>
                    <TableCell className='table-cell administrator-table-cell' align="left">{data.email}</TableCell>
                    <TableCell className='table-cell administrator-table-cell' align="left">{handleStatus(data.access)}</TableCell>
                    <TableCell className='table-cell administrator-table-cell' style={{ width: '80px' }} align="left">
                      <div className='icons-container'>
                        <MoreVertRounded 
                          aria-controls={open ? 'fade-menu' : undefined} 
                          aria-haspopup="true" 
                          aria-expanded={open ? 'true' : undefined} 
                          onClick={(e) => {
                            setSelectedAdmin(data);
                            handleClick(e);
                          }} 
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={4} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 500 }}>
            <TableBody>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={3}
                  count={displayedData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page',
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Menu id="fade-menu" MenuListProps={{ 'aria-labelledby': 'fade-button' }} anchorEl={anchorEl} open={open} onClose={handleClose} TransitionComponent={Fade}>
        <MenuItem onClick={() => handleModal('revoke-administrator', selectedAdmin)}> Revoke Access </MenuItem>
        <MenuItem onClick={() => handleModal('make-superadmin', selectedAdmin)} disabled={selectedAdmin?.access === 'superadmin'}> Make Super Admin </MenuItem>
      </Menu>
    </>
  );
}
